import { useEffect, useMemo, useState } from 'react';
import type { ProductInformation } from '@hotjar/product-catalog';
import { ProductCatalogRepository, useProductCatalog } from '@hotjar/product-catalog';
import type { Organization, Site } from '@hotjar/service-management';
import { useServiceResources as useServiceManagementResources } from '@hotjar/service-management';
import type { BillingPeriod, ProductAllowance } from '../../../api/common/model/subscription';
import type { CostSummary } from '../../../api/common/model/cost';
import { productsSortingFunction } from '~/subscription-management/utils/function';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import { mapProductAllowancesToEntityProduct } from '~/subscription-management/utils/product.utils';
import { getBundleDiscount, getLegacyDiscount } from '~/subscription-management/utils/discounts';
import type { DiscountState } from '~/subscription-management/utils/discounts';

interface ServiceResourceListResult {
  allResources: OrganizationListItem[] | undefined;
  loading: boolean;
}

export interface SiteItem extends Site {
  products: {
    renewWith: ProductInformation | undefined;
    effectiveNow: ProductInformation;
  }[];
  legacyDiscount: DiscountState;
  bundleDiscount: DiscountState;
}

export interface OrganizationListItem extends Organization {
  sites: SiteItem[];
}

interface Props {
  userId: number;
  accountId: number;
  productAllowances: {
    renewWith: ProductAllowance[];
    effectiveNow: ProductAllowance[];
  };
  billingPeriod: BillingPeriod;
  currency: Currency;
  costs: {
    current?: CostSummary;
    future?: CostSummary;
  };
}

/**
 *
 * TODO:
 *  - handle errors that comes from the external queries
 */
export const useServiceResourceList = ({
  userId,
  accountId,
  productAllowances,
  billingPeriod,
  currency,
  costs,
}: Props): ServiceResourceListResult => {
  const [productCatalogRepository, setProductCatalogRepository] = useState<
    ProductCatalogRepository | undefined
  >();

  const { data: resources, isLoading: isResourcesLoading } = useServiceManagementResources(
    userId,
    accountId,
    true,
  );
  const { data: productCatalog, isLoading: isProductCatalogLoading } = useProductCatalog({
    billingPeriod,
    currency,
  });

  useEffect(() => {
    if (productCatalog) {
      setProductCatalogRepository(new ProductCatalogRepository(productCatalog));
    }
  }, [productCatalog]);

  const mapProductsToResources = useMemo<OrganizationListItem[] | undefined>(() => {
    if (
      productAllowances === undefined ||
      resources === undefined ||
      productCatalogRepository === undefined
    ) {
      return undefined;
    }

    return resources.map((organization) => ({
      ...organization,
      sites: organization.sites.map((site) => {
        const effectiveNow = productAllowances.effectiveNow
          .filter((product) => product.entity.entityIdentifier === site.id)
          .map((product) => productCatalogRepository.getProductBySku(product.sku))
          .sort(productsSortingFunction) as ProductInformation[];

        const renewWith = productAllowances.renewWith
          .filter((product) => product.entity.entityIdentifier === site.id)
          .map((product) => productCatalogRepository.getProductBySku(product.sku))
          .sort(productsSortingFunction) as ProductInformation[];

        return {
          ...site,
          products: mapProductAllowancesToEntityProduct({
            effectiveNow,
            renewWith,
          }),
          legacyDiscount: getLegacyDiscount(
            site.id,
            costs.current?.purchasedProductCosts,
            costs.future?.purchasedProductCosts,
          ),
          bundleDiscount: getBundleDiscount(
            site.id,
            costs.current?.purchasedProductCosts,
            costs.future?.purchasedProductCosts,
          ),
        };
      }),
    }));
  }, [productCatalogRepository, productAllowances, resources, costs]);

  return {
    allResources: mapProductsToResources,
    loading: isResourcesLoading || isProductCatalogLoading,
  };
};
